/**
 * テーブル詳細モーダルのカラム行表示。
 * 編集権限保持者にはタグ削除 / 説明文インライン編集 / dead トグル / +edge ボタンを出す。
 */
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import type { TFunction } from 'i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import type { ColumnInfo, UpdateColumnReviewInput } from '../../../types/db-dictionary.js';
import { EdgeTag } from './EdgeTag.js';

interface ColumnRowProps {
  col: ColumnInfo;
  canEdit: boolean;
  editingColumn: string | null;
  editValue: string;
  setEditValue: (v: string) => void;
  setEditingColumn: (name: string | null) => void;
  setEnumPopup: (enumName: string | null) => void;
  setAddEdgePopup: (
    state: { defaultEdgeType: 'REFERENCES' | 'USES_ENUM'; sourceColumn: string } | null,
  ) => void;
  updateColumnReview: UseMutationResult<
    unknown,
    Error,
    { schema: string; table: string; column: string; data: UpdateColumnReviewInput }
  >;
  deleteEdge: UseMutationResult<unknown, Error, string>;
  handleSaveColumnDesc: (colName: string) => void;
  handleToggleColumnDead: (colName: string, currentIsDead: boolean) => void;
  t: TFunction;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business テーブル詳細モーダル内のカラム単位 UI。FK/SameEntity/Enum タグ表示・edge追加導線・dead 切替・説明文編集
 * @graph-connects EdgeTag [contains] FK/SameEntity/Enum タグの描画コンポーネントを内包
 */
export function ColumnRow({
  col,
  canEdit,
  editingColumn,
  editValue,
  setEditValue,
  setEditingColumn,
  setEnumPopup,
  setAddEdgePopup,
  updateColumnReview,
  deleteEdge,
  handleSaveColumnDesc,
  handleToggleColumnDead,
  t,
}: ColumnRowProps) {
  return (
    <div
      className={`rounded-lg border border-[var(--color-border)] p-3 ${col.isDead ? 'bg-[var(--color-error)]/5 border-[var(--color-error)]/20' : 'bg-[var(--color-bg-elevated)]'}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium text-[var(--color-text-primary)]">
            {col.name}
          </span>
          <Badge variant="default" size="sm">
            {col.dataType}
          </Badge>
          {col.nullable && <span className="text-xs text-[var(--color-text-muted)]">NULL</span>}
          {col.fkTarget && (
            <EdgeTag
              variant="fk"
              label={`FK → ${col.fkTarget}.${col.fkColumn ?? 'id'}`}
              canEdit={canEdit && !!col.fkEdgeId}
              isDeleting={deleteEdge.isPending}
              onDelete={((edgeId: string | null | undefined) =>
                edgeId
                  ? () => {
                      deleteEdge.mutate(edgeId);
                    }
                  : undefined)(col.fkEdgeId)}
            />
          )}
          {col.sameEntities?.map((se) => (
            <EdgeTag
              key={se.edgeId}
              variant="same"
              label={`≡ ${se.linkedColumn}`}
              title={se.note ?? undefined}
              canEdit={canEdit}
              isDeleting={deleteEdge.isPending}
              onDelete={() => {
                deleteEdge.mutate(se.edgeId);
              }}
            />
          ))}
          {col.usesEnum && (
            <EdgeTag
              variant="enum"
              label={`${t('dbDictionary.edges.usesEnum')}: ${col.usesEnum.enumName}`}
              title={col.usesEnum.enumValues ?? ''}
              canEdit={canEdit}
              isDeleting={deleteEdge.isPending}
              onClick={() => {
                if (col.usesEnum) setEnumPopup(col.usesEnum.enumName);
              }}
              onDelete={() => {
                if (col.usesEnum) deleteEdge.mutate(col.usesEnum.edgeId);
              }}
            />
          )}
          {canEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setAddEdgePopup({
                  defaultEdgeType: col.usesEnum ? 'REFERENCES' : 'USES_ENUM',
                  sourceColumn: col.name,
                });
              }}
              className="text-xs px-1.5 py-0.5 rounded border border-dashed border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)] transition-colors"
              title="この列にエッジを追加"
            >
              + edge
            </button>
          )}
          {col.isDead && (
            <Badge variant="error" size="sm">
              {t('dbDictionary.column.dead')}
              {col.deadEmptyPct !== null ? ` ${String(col.deadEmptyPct)}%` : ''}
            </Badge>
          )}
        </div>
        {canEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleColumnDead(col.name, col.isDead);
            }}
            className={`text-xs px-2 py-0.5 rounded transition-colors ${
              col.isDead
                ? 'text-[var(--color-success)] hover:bg-[var(--color-success)]/10'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-error)]/10'
            }`}
          >
            {col.isDead ? t('dbDictionary.table.alive') : t('dbDictionary.table.markAsDead')}
          </button>
        )}
      </div>

      {canEdit && editingColumn === col.name ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
            }}
            rows={2}
            className="w-full rounded-lg border border-[var(--color-border-focus)] bg-[var(--color-bg-base)] px-3 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none resize-y"
            autoFocus
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              loading={updateColumnReview.isPending}
              onClick={() => {
                handleSaveColumnDesc(col.name);
              }}
            >
              {t('dbDictionary.actions.save')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingColumn(null);
              }}
            >
              {t('dbDictionary.actions.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <p
          className={`text-xs text-[var(--color-text-secondary)] transition-colors ${canEdit ? 'cursor-pointer hover:text-[var(--color-text-primary)]' : ''}`}
          onClick={
            canEdit
              ? () => {
                  setEditValue(col.humanDescription ?? col.description ?? '');
                  setEditingColumn(col.name);
                }
              : undefined
          }
        >
          {col.humanDescription ?? col.description ?? '-'}
          {col.isDead && col.deadNote && (
            <span className="ml-2 text-[var(--color-error)]">({col.deadNote})</span>
          )}
        </p>
      )}
    </div>
  );
}
